import { useState, useEffect } from 'react'
import { useNavigate, Link, useSearchParams } from 'react-router-dom'
import { authApi, projectsApi, projectPlansApi, calendarApi, siteSettingsApi } from '../services/api'
import ProjectPlanCard from '../components/ProjectPlanCard'
import CalendarView from '../components/CalendarView'
import Modal from '../components/Modal'
import AdminSidebar from '../components/AdminSidebar'
import EmptyState from '../components/EmptyState'
import { ClipboardIcon, CheckIcon, XIcon, UploadIcon, FolderIcon, IdeaIcon, DragIcon } from '../components/Icons'
import { useToast } from '../components/ToastContainer'
import '../styles/Elise.css'

function Elise() {
  const [loading, setLoading] = useState(true)
  const [user, setUser] = useState(null)
  const [projects, setProjects] = useState([])
  const [projectPlans, setProjectPlans] = useState([])
  const [calendarEvents, setCalendarEvents] = useState([])
  const [siteSettings, setSiteSettings] = useState(null)
  const [activeView, setActiveView] = useState('plans') // plans, calendar
  const [selectedProject, setSelectedProject] = useState(null)
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const toast = useToast()

  // Modal states
  const [showPlanModal, setShowPlanModal] = useState(false)
  const [showEventModal, setShowEventModal] = useState(false)
  const [showImportModal, setShowImportModal] = useState(false)
  const [showTemplateModal, setShowTemplateModal] = useState(false)

  // Apple Calendar sync states
  const [syncing, setSyncing] = useState(false)
  const [syncMessage, setSyncMessage] = useState('')

  // Import states
  const [importFile, setImportFile] = useState(null)
  const [importMode, setImportMode] = useState('file') // 'file' or 'paste'
  const [importText, setImportText] = useState('')
  const [importLoading, setImportLoading] = useState(false)
  const [importMessage, setImportMessage] = useState('')

  // Plan creation states
  const [planLoading, setPlanLoading] = useState(false)
  const [planMessage, setPlanMessage] = useState('')

  // Event creation states
  const [eventLoading, setEventLoading] = useState(false)
  const [eventMessage, setEventMessage] = useState('')

  // Drag and drop states
  const [draggedPlan, setDraggedPlan] = useState(null)
  const [dragOverPlan, setDragOverPlan] = useState(null)

  // Form states
  const [planForm, setPlanForm] = useState({
    project_id: '',
    title: '',
    description: '',
    objectives: '',
    notes: ''
  })

  const [eventForm, setEventForm] = useState({
    project_id: '',
    title: '',
    start_time: '',
    end_time: '',
    event_type: 'work',
    color: '#3b82f6',
    description: ''
  })

  // Check authentication on mount
  useEffect(() => {
    const checkAuth = async () => {
      const token = localStorage.getItem('auth_token')
      if (!token) {
        navigate('/admin/login')
        return
      }

      try {
        const response = await authApi.verify()
        setUser(response.data.user)
        setLoading(false)
        loadData()
      } catch (error) {
        console.error('Auth verification failed:', error)
        localStorage.removeItem('auth_token')
        navigate('/admin/login')
      }
    }

    checkAuth()
  }, [navigate])

  // Handle deep linking from URL parameters
  useEffect(() => {
    const view = searchParams.get('view')
    if (view === 'plans' || view === 'calendar') {
      setActiveView(view)
    }
  }, [searchParams])

  const loadData = async () => {
    try {
      const [projectsRes, plansRes, eventsRes, settingsRes] = await Promise.all([
        projectsApi.getAll(),
        projectPlansApi.getAll(),
        calendarApi.getAll(),
        siteSettingsApi.get()
      ])

      setProjects(projectsRes.data)
      // Sort plans by sort_order
      const sortedPlans = [...plansRes.data].sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0))
      setProjectPlans(sortedPlans)
      setCalendarEvents(eventsRes.data)
      setSiteSettings(settingsRes.data)
    } catch (error) {
      console.error('Error loading data:', error)
    }
  }

  const handleLogout = async () => {
    try {
      await authApi.logout()
      navigate('/')
    } catch (error) {
      console.error('Logout error:', error)
    }
  }

  // Modal handlers
  const handleCreatePlan = async (e) => {
    e.preventDefault()
    setPlanLoading(true)
    setPlanMessage('')

    try {
      const planData = {
        ...planForm,
        objectives: planForm.objectives.split('\n').filter(o => o.trim()),
        milestones: [],
        tasks: []
      }
      await projectPlansApi.create(planData)
      setPlanMessage('Project plan created successfully!')
      setPlanForm({ project_id: '', title: '', description: '', objectives: '', notes: '' })
      loadData()

      setTimeout(() => {
        setShowPlanModal(false)
        setPlanMessage('')
      }, 1500)
    } catch (error) {
      console.error('Error creating plan:', error)
      setPlanMessage('Failed to create plan: ' + (error.response?.data?.detail || error.message))
    } finally {
      setPlanLoading(false)
    }
  }

  const handleCreateEvent = async (e) => {
    e.preventDefault()
    setEventLoading(true)
    setEventMessage('')

    try {
      await calendarApi.create(eventForm)
      setEventMessage('Calendar event created successfully!')
      setEventForm({
        project_id: '',
        title: '',
        start_time: '',
        end_time: '',
        event_type: 'work',
        color: '#3b82f6',
        description: ''
      })
      loadData()

      setTimeout(() => {
        setShowEventModal(false)
        setEventMessage('')
      }, 1500)
    } catch (error) {
      console.error('Error creating event:', error)
      setEventMessage('Failed to create event: ' + (error.response?.data?.detail || error.message))
    } finally {
      setEventLoading(false)
    }
  }

  const handleSyncAppleCalendar = async () => {
    setSyncing(true)
    setSyncMessage('')

    try {
      const response = await calendarApi.syncWithAppleCalendar()
      const result = response.data

      if (result.success) {
        setSyncMessage(`Synced ${result.total_synced} events (${result.created} created, ${result.updated} updated)`)
        loadData() // Reload calendar events to show synced data
      } else {
        setSyncMessage('Sync failed: ' + result.message)
      }

      // Auto-hide message after 5 seconds
      setTimeout(() => setSyncMessage(''), 5000)
    } catch (error) {
      console.error('Error syncing with Apple Calendar:', error)
      setSyncMessage('Sync failed: ' + (error.response?.data?.detail || error.message))
      setTimeout(() => setSyncMessage(''), 5000)
    } finally {
      setSyncing(false)
    }
  }

  const handleSyncPublicCalendar = async () => {
    setSyncing(true)
    setSyncMessage('')

    try {
      const response = await calendarApi.syncWithPublicCalendar()
      const result = response.data

      if (result.success) {
        setSyncMessage(`Synced ${result.total_synced} events from "${result.calendar_name}" (${result.created} created, ${result.updated} updated)`)
        loadData() // Reload calendar events to show synced data
      } else {
        setSyncMessage('Sync failed: ' + result.message)
      }

      // Auto-hide message after 5 seconds
      setTimeout(() => setSyncMessage(''), 5000)
    } catch (error) {
      console.error('Error syncing with public calendar:', error)
      setSyncMessage('Sync failed: ' + (error.response?.data?.detail || error.message))
      setTimeout(() => setSyncMessage(''), 5000)
    } finally {
      setSyncing(false)
    }
  }

  // Determine which calendar sync is configured
  const hasCalDAVConfig = siteSettings?.apple_calendar_username && siteSettings?.apple_calendar_password
  const hasPublicCalendarConfig = siteSettings?.apple_calendar_public_url

  const handleImportPlan = async (e) => {
    e.preventDefault()

    // Validate input based on mode
    if (importMode === 'file' && !importFile) {
      setImportMessage('Please select a file to upload')
      return
    }

    if (importMode === 'paste' && !importText.trim()) {
      setImportMessage('Please paste JSON or CSV content')
      return
    }

    setImportLoading(true)
    setImportMessage('')

    try {
      const formData = new FormData()

      if (importMode === 'file') {
        formData.append('file', importFile)
      } else {
        // Create a file from pasted text
        // Try to detect if it's JSON or CSV
        const trimmedText = importText.trim()
        let filename = 'pasted-plan.json'
        let mimeType = 'application/json'

        try {
          // Try to parse as JSON
          JSON.parse(trimmedText)
        } catch (e) {
          // If not JSON, assume CSV
          filename = 'pasted-plan.csv'
          mimeType = 'text/csv'
        }

        const blob = new Blob([importText], { type: mimeType })
        const file = new File([blob], filename, { type: mimeType })
        formData.append('file', file)
      }

      const response = await projectPlansApi.upload(formData)
      const result = response.data

      // Check if there were any errors during import
      if (result.errors && result.errors.length > 0) {
        if (result.imported === 0) {
          // All imports failed
          setImportMessage(`Import failed: ${result.errors.join('; ')}`)
        } else {
          // Some imports succeeded, some failed
          setImportMessage(`Imported ${result.imported} plan(s). Errors: ${result.errors.join('; ')}`)
          setImportFile(null)
          setImportText('')
          loadData()
        }
      } else if (result.imported === 0) {
        // No errors but also no imports - likely empty file or wrong format
        setImportMessage('No plans were imported. Please check that your file contains valid project plan data with at least a "title" field.')
      } else {
        // Success - all imports worked
        setImportMessage(`Successfully imported ${result.imported} plan(s)!`)
        setImportFile(null)
        setImportText('')
        loadData()

        setTimeout(() => {
          setShowImportModal(false)
          setImportMessage('')
          setImportMode('file')
        }, 2000)
      }
    } catch (error) {
      console.error('Error importing plan:', error)
      setImportMessage('Import failed: ' + (error.response?.data?.detail || error.message))
    } finally {
      setImportLoading(false)
    }
  }

  const handleFileChange = (e) => {
    const file = e.target.files[0]
    if (file) {
      const validTypes = ['application/json', 'text/csv', 'application/vnd.ms-excel']
      if (validTypes.includes(file.type) || file.name.endsWith('.json') || file.name.endsWith('.csv')) {
        setImportFile(file)
        setImportMessage('')
      } else {
        setImportMessage('Please select a valid JSON or CSV file')
        setImportFile(null)
      }
    }
  }

  // Drag and drop handlers
  const handleDragStart = (e, plan) => {
    setDraggedPlan(plan)
    e.dataTransfer.effectAllowed = 'move'
    e.dataTransfer.setData('text/plain', plan.id)
    // Add a slight delay to allow the drag image to be captured
    setTimeout(() => {
      e.target.closest('.plan-drag-wrapper')?.classList.add('dragging')
    }, 0)
  }

  const handleDragEnd = (e) => {
    setDraggedPlan(null)
    setDragOverPlan(null)
    e.target.closest('.plan-drag-wrapper')?.classList.remove('dragging')
  }

  const handleDragOver = (e, plan) => {
    e.preventDefault()
    e.dataTransfer.dropEffect = 'move'
    if (draggedPlan && plan.id !== draggedPlan.id) {
      setDragOverPlan(plan.id)
    }
  }

  const handleDragLeave = (e) => {
    // Only clear if we're leaving the wrapper, not entering a child
    if (!e.currentTarget.contains(e.relatedTarget)) {
      setDragOverPlan(null)
    }
  }

  const handleDrop = async (e, targetPlan) => {
    e.preventDefault()
    setDragOverPlan(null)

    if (!draggedPlan || draggedPlan.id === targetPlan.id) {
      return
    }

    // Find indices
    const draggedIndex = projectPlans.findIndex(p => p.id === draggedPlan.id)
    const targetIndex = projectPlans.findIndex(p => p.id === targetPlan.id)

    if (draggedIndex === -1 || targetIndex === -1) {
      return
    }

    // Reorder the plans array
    const newPlans = [...projectPlans]
    newPlans.splice(draggedIndex, 1)
    newPlans.splice(targetIndex, 0, draggedPlan)

    // Update local state immediately for smooth UX
    setProjectPlans(newPlans)

    // Build the order array with new sort_order values
    const orderUpdate = newPlans.map((plan, index) => ({
      id: plan.id,
      sort_order: index
    }))

    // Save to backend
    try {
      await projectPlansApi.reorder(orderUpdate)
      toast.success('Plan order updated')
    } catch (error) {
      console.error('Error saving plan order:', error)
      toast.error('Failed to save order')
      // Reload to restore correct order
      loadData()
    }
  }

  if (loading) {
    return <div className="loading">Loading...</div>
  }

  if (!user) {
    return null
  }

  return (
    <div className="admin-panel">
      <AdminSidebar currentPage="elise" />

      <div className="admin-content elise-content">
        {activeView === 'plans' && (
          <div className="project-plans-view">
            <div className="elise-header">
              <h1>Project Plans</h1>
              <div className="header-actions">
                <button
                  className="btn btn-primary"
                  onClick={() => setShowPlanModal(true)}
                >
                  New Plan
                </button>
                <button
                  className="btn btn-secondary"
                  onClick={() => setShowImportModal(true)}
                >
                  Import Plan
                </button>
                <button
                  className="btn btn-secondary"
                  onClick={() => setShowTemplateModal(true)}
                  title="View import format template"
                  style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}
                >
                  <ClipboardIcon size={16} /> Template
                </button>
              </div>
            </div>
            <div className="project-plans-grid">
              {projectPlans.length === 0 ? (
                <EmptyState
                  icon="clipboard"
                  title="No Project Plans Yet"
                  message="Get started by creating your first project plan, importing existing plans, or using a template."
                  actionLabel="Create New Plan"
                  onAction={() => setShowPlanModal(true)}
                  secondaryActionLabel="Import Plans"
                  onSecondaryAction={() => setShowImportModal(true)}
                />
              ) : (
                projectPlans.map((plan) => {
                  const project = projects.find(p => p.id === plan.project_id)
                  return (
                    <div
                      key={plan.id}
                      className={`plan-drag-wrapper ${dragOverPlan === plan.id ? 'drag-over' : ''} ${draggedPlan?.id === plan.id ? 'dragging' : ''}`}
                      draggable
                      onDragStart={(e) => handleDragStart(e, plan)}
                      onDragEnd={handleDragEnd}
                      onDragOver={(e) => handleDragOver(e, plan)}
                      onDragLeave={handleDragLeave}
                      onDrop={(e) => handleDrop(e, plan)}
                    >
                      <div className="drag-handle" title="Drag to reorder">
                        <DragIcon size={16} />
                      </div>
                      <ProjectPlanCard
                        plan={plan}
                        project={project}
                        onReload={loadData}
                      />
                    </div>
                  )
                })
              )}
            </div>
          </div>
        )}

        {activeView === 'calendar' && (
          <div className="calendar-view">
            <div className="elise-header">
              <h1>Calendar</h1>
              <div className="header-actions">
                {hasPublicCalendarConfig && (
                  <button
                    className="btn btn-secondary"
                    onClick={handleSyncPublicCalendar}
                    disabled={syncing}
                  >
                    {syncing ? 'Syncing...' : 'Sync from Calendar'}
                  </button>
                )}
                {hasCalDAVConfig && !hasPublicCalendarConfig && (
                  <button
                    className="btn btn-secondary"
                    onClick={handleSyncAppleCalendar}
                    disabled={syncing}
                  >
                    {syncing ? 'Syncing...' : 'Sync with Apple Calendar'}
                  </button>
                )}
                <button
                  className="btn btn-primary"
                  onClick={() => setShowEventModal(true)}
                >
                  New Event
                </button>
              </div>
            </div>
            {syncMessage && (
              <div
                style={{
                  marginBottom: '16px',
                  padding: '12px',
                  borderRadius: '6px',
                  backgroundColor: syncMessage.includes('failed') ? '#fee2e2' : '#d1fae5',
                  color: syncMessage.includes('failed') ? '#991b1b' : '#065f46',
                  fontSize: '14px',
                  animation: 'fadeIn 0.3s'
                }}
              >
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                  {syncMessage.includes('failed') ? <XIcon size={16} /> : <CheckIcon size={16} />} {syncMessage}
                </span>
              </div>
            )}
            <CalendarView
              events={calendarEvents}
              onReload={loadData}
              projectPlans={projectPlans}
              projects={projects}
            />
          </div>
        )}
      </div>

      {/* Create Plan Modal */}
      <Modal
        isOpen={showPlanModal}
        onClose={() => setShowPlanModal(false)}
        title="Create Project Plan"
      >
        <form onSubmit={handleCreatePlan}>
          <div className="modal-body">
            <div className="form-group">
              <label htmlFor="plan-project">Project (Optional)</label>
              <select
                id="plan-project"
                value={planForm.project_id}
                onChange={(e) => setPlanForm({ ...planForm, project_id: e.target.value })}
              >
                <option value="">No project</option>
                {projects.map(project => (
                  <option key={project.id} value={project.id}>{project.title}</option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="plan-title">Plan Title</label>
              <input
                type="text"
                id="plan-title"
                value={planForm.title}
                onChange={(e) => setPlanForm({ ...planForm, title: e.target.value })}
                placeholder="e.g., Q1 Development Plan"
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="plan-description">Description</label>
              <textarea
                id="plan-description"
                value={planForm.description}
                onChange={(e) => setPlanForm({ ...planForm, description: e.target.value })}
                placeholder="Brief description of this plan..."
                rows="3"
              />
            </div>

            <div className="form-group">
              <label htmlFor="plan-objectives">Objectives</label>
              <textarea
                id="plan-objectives"
                value={planForm.objectives}
                onChange={(e) => setPlanForm({ ...planForm, objectives: e.target.value })}
                placeholder="Enter one objective per line..."
                rows="4"
              />
              <small>Enter one objective per line</small>
            </div>

            <div className="form-group">
              <label htmlFor="plan-notes">Notes</label>
              <textarea
                id="plan-notes"
                value={planForm.notes}
                onChange={(e) => setPlanForm({ ...planForm, notes: e.target.value })}
                placeholder="Additional notes..."
                rows="3"
              />
            </div>

            {planMessage && (
              <div
                style={{
                  padding: '12px',
                  borderRadius: '6px',
                  marginTop: '16px',
                  backgroundColor: planMessage.includes('Failed') || planMessage.includes('Error')
                    ? 'var(--danger-bg)'
                    : 'var(--success-bg)',
                  color: planMessage.includes('Failed') || planMessage.includes('Error')
                    ? 'var(--danger-text)'
                    : 'var(--success-text)',
                  fontSize: '14px',
                  fontWeight: '500'
                }}
              >
                {planMessage}
              </div>
            )}
          </div>

          <div className="form-actions">
            <button type="button" className="btn btn-secondary" onClick={() => setShowPlanModal(false)} disabled={planLoading}>
              Cancel
            </button>
            <button type="submit" className="btn btn-primary" disabled={planLoading}>
              {planLoading ? 'Creating...' : 'Create Plan'}
            </button>
          </div>
        </form>
      </Modal>

      {/* Create Event Modal */}
      <Modal
        isOpen={showEventModal}
        onClose={() => setShowEventModal(false)}
        title="Create Calendar Event"
      >
        <form onSubmit={handleCreateEvent}>
          <div className="modal-body">
            <div className="form-group">
              <label htmlFor="event-title">Event Title</label>
              <input
                type="text"
                id="event-title"
                value={eventForm.title}
                onChange={(e) => setEventForm({ ...eventForm, title: e.target.value })}
                placeholder="e.g., Design Review Meeting"
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="event-project">Link to Project (Optional)</label>
              <select
                id="event-project"
                value={eventForm.project_id}
                onChange={(e) => setEventForm({ ...eventForm, project_id: e.target.value })}
              >
                <option value="">No project</option>
                {projects.map(project => (
                  <option key={project.id} value={project.id}>{project.title}</option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="event-start">Start Time</label>
              <input
                type="datetime-local"
                id="event-start"
                value={eventForm.start_time}
                onChange={(e) => setEventForm({ ...eventForm, start_time: e.target.value })}
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="event-end">End Time</label>
              <input
                type="datetime-local"
                id="event-end"
                value={eventForm.end_time}
                onChange={(e) => setEventForm({ ...eventForm, end_time: e.target.value })}
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="event-type">Event Type</label>
              <select
                id="event-type"
                value={eventForm.event_type}
                onChange={(e) => setEventForm({ ...eventForm, event_type: e.target.value })}
              >
                <option value="work">Work</option>
                <option value="meeting">Meeting</option>
                <option value="deadline">Deadline</option>
                <option value="personal">Personal</option>
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="event-color">Color</label>
              <input
                type="color"
                id="event-color"
                value={eventForm.color}
                onChange={(e) => setEventForm({ ...eventForm, color: e.target.value })}
              />
            </div>

            <div className="form-group">
              <label htmlFor="event-description">Description</label>
              <textarea
                id="event-description"
                value={eventForm.description}
                onChange={(e) => setEventForm({ ...eventForm, description: e.target.value })}
                placeholder="Event details..."
                rows="3"
              />
            </div>

            {eventMessage && (
              <div
                style={{
                  padding: '12px',
                  borderRadius: '6px',
                  marginTop: '16px',
                  backgroundColor: eventMessage.includes('Failed') || eventMessage.includes('Error')
                    ? 'var(--danger-bg)'
                    : 'var(--success-bg)',
                  color: eventMessage.includes('Failed') || eventMessage.includes('Error')
                    ? 'var(--danger-text)'
                    : 'var(--success-text)',
                  fontSize: '14px',
                  fontWeight: '500'
                }}
              >
                {eventMessage}
              </div>
            )}
          </div>

          <div className="form-actions">
            <button type="button" className="btn btn-secondary" onClick={() => setShowEventModal(false)} disabled={eventLoading}>
              Cancel
            </button>
            <button type="submit" className="btn btn-primary" disabled={eventLoading}>
              {eventLoading ? 'Creating...' : 'Create Event'}
            </button>
          </div>
        </form>
      </Modal>

      {/* Import Plan Modal */}
      <Modal
        isOpen={showImportModal}
        onClose={() => {
          setShowImportModal(false)
          setImportFile(null)
          setImportText('')
          setImportMessage('')
          setImportMode('file')
        }}
        title="Import Project Plan"
      >
        <form onSubmit={handleImportPlan}>
          <div className="modal-body">
            {/* Mode Toggle */}
            <div style={{ display: 'flex', gap: '8px', marginBottom: '20px', borderBottom: '2px solid var(--border-color)' }}>
              <button
                type="button"
                onClick={() => setImportMode('file')}
                style={{
                  padding: '12px 24px',
                  background: 'transparent',
                  border: 'none',
                  borderBottom: importMode === 'file' ? '3px solid var(--primary-color)' : '3px solid transparent',
                  color: importMode === 'file' ? 'var(--primary-color)' : 'var(--text-secondary)',
                  fontWeight: importMode === 'file' ? '600' : '400',
                  cursor: 'pointer',
                  marginBottom: '-2px',
                  transition: 'all 0.2s'
                }}
              >
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}><UploadIcon size={16} /> Upload File</span>
              </button>
              <button
                type="button"
                onClick={() => setImportMode('paste')}
                style={{
                  padding: '12px 24px',
                  background: 'transparent',
                  border: 'none',
                  borderBottom: importMode === 'paste' ? '3px solid var(--primary-color)' : '3px solid transparent',
                  color: importMode === 'paste' ? 'var(--primary-color)' : 'var(--text-secondary)',
                  fontWeight: importMode === 'paste' ? '600' : '400',
                  cursor: 'pointer',
                  marginBottom: '-2px',
                  transition: 'all 0.2s'
                }}
              >
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}><ClipboardIcon size={16} /> Paste Text</span>
              </button>
            </div>

            <p style={{ marginBottom: '16px', color: 'var(--text-secondary)' }}>
              {importMode === 'file'
                ? 'Upload a JSON or CSV file containing your project plan. Need help with the format? Click the "Template" button to see the required structure.'
                : 'Paste JSON or CSV content directly. Need help with the format? Click the "Template" button to see the required structure.'
              }
            </p>

            {importMode === 'file' ? (
              <div className="form-group">
                <label htmlFor="import-file">Select File</label>
                <input
                  type="file"
                  id="import-file"
                  accept=".json,.csv"
                  onChange={handleFileChange}
                  style={{
                    padding: '8px',
                    border: '2px solid var(--border-color)',
                    borderRadius: '6px',
                    width: '100%'
                  }}
                />
                {importFile && (
                  <small style={{ color: 'var(--success-color)', marginTop: '8px', display: 'block' }}>
                    Selected: {importFile.name}
                  </small>
                )}
              </div>
            ) : (
              <div className="form-group">
                <label htmlFor="import-text">Paste JSON or CSV Content</label>
                <textarea
                  id="import-text"
                  value={importText}
                  onChange={(e) => setImportText(e.target.value)}
                  placeholder='Paste your JSON or CSV content here...\n\nExample JSON:\n{\n  "title": "My Project",\n  "description": "Project description"\n}'
                  style={{
                    width: '100%',
                    minHeight: '300px',
                    padding: '12px',
                    border: '2px solid var(--border-color)',
                    borderRadius: '6px',
                    fontFamily: 'monospace',
                    fontSize: '13px',
                    resize: 'vertical',
                    background: 'var(--input-bg)',
                    color: 'var(--text-primary)'
                  }}
                />
                {importText && (
                  <small style={{ color: 'var(--success-color)', marginTop: '8px', display: 'block' }}>
                    {importText.length} characters
                  </small>
                )}
              </div>
            )}

            {importMessage && (
              <div
                style={{
                  padding: '12px',
                  borderRadius: '6px',
                  marginTop: '12px',
                  backgroundColor: importMessage.includes('failed') || importMessage.includes('select') || importMessage.includes('Error') || importMessage.includes('Invalid') || importMessage.includes('No plans were imported')
                    ? 'var(--danger-bg, #fee2e2)'
                    : importMessage.includes('Errors:')
                      ? 'var(--warning-bg, #fef3c7)'
                      : 'var(--success-bg, #d1fae5)',
                  color: importMessage.includes('failed') || importMessage.includes('select') || importMessage.includes('Error') || importMessage.includes('Invalid') || importMessage.includes('No plans were imported')
                    ? 'var(--danger-text, #991b1b)'
                    : importMessage.includes('Errors:')
                      ? 'var(--warning-text, #92400e)'
                      : 'var(--success-text, #065f46)',
                  fontSize: '14px',
                  fontWeight: '500',
                  maxHeight: '150px',
                  overflowY: 'auto'
                }}
              >
                {importMessage}
              </div>
            )}
          </div>

          <div className="form-actions">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => {
                setShowImportModal(false)
                setImportFile(null)
                setImportText('')
                setImportMessage('')
                setImportMode('file')
              }}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={
                importLoading ||
                (importMode === 'file' && !importFile) ||
                (importMode === 'paste' && !importText.trim())
              }
            >
              {importLoading ? 'Importing...' : 'Import'}
            </button>
          </div>
        </form>
      </Modal>

      {/* Template Modal */}
      <Modal
        isOpen={showTemplateModal}
        onClose={() => setShowTemplateModal(false)}
        title="Claude Import Template"
      >
        <div className="modal-body" style={{ maxHeight: '70vh', overflowY: 'auto' }}>
          <p style={{ marginBottom: '16px', color: 'var(--text-secondary)' }}>
            Copy this prompt and provide it to Claude along with your project description to generate a properly formatted project plan:
          </p>

          <div style={{
            backgroundColor: 'var(--bg-secondary)',
            padding: '16px',
            borderRadius: '8px',
            marginBottom: '16px',
            position: 'relative'
          }}>
            <button
              onClick={() => {
                const template = document.getElementById('claude-template').textContent
                navigator.clipboard.writeText(template)
                toast.success('Template copied to clipboard!')
              }}
              style={{
                position: 'absolute',
                top: '8px',
                right: '8px',
                padding: '6px 12px',
                backgroundColor: 'var(--primary-color)',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '12px'
              }}
            >
              📋 Copy
            </button>
            <pre id="claude-template" style={{
              whiteSpace: 'pre-wrap',
              fontFamily: 'monospace',
              fontSize: '13px',
              lineHeight: '1.6',
              margin: 0,
              color: 'var(--text-primary)'
            }}>
{`Please create a project plan for [PROJECT DESCRIPTION] in JSON format following this schema:

{
  "title": "string (required) - Clear, concise project title",
  "description": "string (optional) - 1-2 sentence overview",
  "project_id": null,
  "objectives": ["array of 3-5 high-level goals"],
  "notes": "string (optional) - Budget, timeline, special considerations",
  "milestones": [
    {
      "title": "Milestone name",
      "description": "What this milestone represents",
      "target_date": "YYYY-MM-DDTHH:MM:SSZ (ISO 8601 format)"
    }
  ],
  "tasks": [
    {
      "title": "Task name (actionable verb)",
      "description": "Specific details about what to do",
      "status": "pending|in_progress|completed",
      "priority": "low|medium|high",
      "due_date": "YYYY-MM-DDTHH:MM:SSZ"
    }
  ]
}

Requirements:
- Include 3-6 major milestones with realistic target dates
- Break down into 10-20 specific, actionable tasks
- Prioritize tasks (high for critical path, medium for important, low for nice-to-have)
- Set realistic due dates in chronological order
- Use active voice for task titles (e.g., "Cut steel tubing" not "Steel tubing cutting")
- Include enough detail in descriptions for someone to execute the plan

Context:
[Add any specific requirements, timeline, budget, or constraints here]`}
            </pre>
          </div>

          <div style={{
            backgroundColor: 'var(--bg-tertiary)',
            padding: '12px',
            borderRadius: '6px',
            fontSize: '13px',
            color: 'var(--text-secondary)'
          }}>
            <strong>💡 Quick Tips:</strong>
            <ul style={{ margin: '8px 0', paddingLeft: '20px' }}>
              <li>Replace [PROJECT DESCRIPTION] with your actual project</li>
              <li>Add context like timeline, budget, and constraints</li>
              <li>Claude will generate a complete JSON file ready to import</li>
              <li>Save the response as a .json file and use "Import Plan"</li>
            </ul>
          </div>
        </div>

        <div className="form-actions">
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => setShowTemplateModal(false)}
          >
            Close
          </button>
        </div>
      </Modal>
    </div>
  )
}

export default Elise
